import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { createAnnotationApi, deleteAnnotationApi, fetchTrace, updateAnnotationApi } from "../api";
import {
  Annotation,
  CreateAnnotationPayload,
  TraceAnnotationCounts,
  TraceDetailResponse,
  TraceStep
} from "../types";
import AnnotationForm from "./forms/AnnotationForm";
import AnnotationList from "./forms/AnnotationList";

interface Props {
  traceId: string;
  onNavigatePrevious?: () => void;
  onNavigateNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  onDeleteTrace?: (id: string) => void;
  isDeletingTrace?: boolean;
  onAnnotationSummaryChange?: (traceId: string, summary: TraceAnnotationCounts | null) => void;
}

export default function TraceViewer({ traceId, onNavigatePrevious, onNavigateNext, hasPrevious = false, hasNext = false, onDeleteTrace, isDeletingTrace, onAnnotationSummaryChange }: Props) {
  const queryClient = useQueryClient();
  const query = useQuery<TraceDetailResponse>({
    queryKey: ["trace", traceId],
    queryFn: () => fetchTrace(traceId),
    enabled: Boolean(traceId)
  });
  const [copied, setCopied] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const gatewayLogBase = import.meta.env.VITE_GATEWAY_LOG_URL as string | undefined;

  useEffect(() => {
    if (!onAnnotationSummaryChange) {
      return;
    }
    if (query.isError) {
      onAnnotationSummaryChange(traceId, null);
      return;
    }
    if (!query.data) {
      return;
    }
    onAnnotationSummaryChange(traceId, summarizeAnnotations(query.data.annotations));
  }, [query.data, query.isError, onAnnotationSummaryChange, traceId]);

  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["trace", traceId] }),
      queryClient.invalidateQueries({ queryKey: ["annotation-summary"] })
    ]);
  };

  const createMutation = useMutation({
    mutationFn: (payload: CreateAnnotationPayload) => createAnnotationApi(payload),
    onSuccess: () => {
      setErrorMessage(null);
      invalidate();
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : "Unable to save annotation.";
      setErrorMessage(message.includes("FOREIGN KEY") ? "Trace is no longer available in the database." : message);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Partial<Annotation> }) => updateAnnotationApi(id, payload),
    onSuccess: () => {
      setErrorMessage(null);
      invalidate();
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteAnnotationApi(id),
    onSuccess: () => {
      setErrorMessage(null);
      invalidate();
    }
  });

  if (query.isLoading) {
    return <div className="viewer__state">Loading trace…</div>;
  }

  if (query.isError || !query.data) {
    return <div className="viewer__state">Unable to load trace.</div>;
  }

  const { trace, steps, toolCalls, annotations } = query.data;
  const createdAt = trace.createdAt ? format(new Date(trace.createdAt), "PPpp") : "";
  const metrics = [
    trace.latencyMs !== undefined ? { label: "Latency", value: `${trace.latencyMs} ms` } : null,
    trace.inputTokens !== undefined || trace.outputTokens !== undefined
      ? { label: "Tokens", value: `${trace.inputTokens ?? 0} in / ${trace.outputTokens ?? 0} out` }
      : null,
    trace.costUsd !== undefined ? { label: "Cost", value: `$${trace.costUsd.toFixed(4)}` } : null
  ].filter(Boolean) as Array<{ label: string; value: string }>;
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(trace.id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      console.error("Failed to copy", error);
    }
  };

  const traceLogLink = (() => {
    if (!gatewayLogBase) return undefined;
    if (gatewayLogBase.includes("%TRACE_ID%")) {
      return gatewayLogBase.replace(/%TRACE_ID%/g, trace.id);
    }
    return `${gatewayLogBase.replace(/\/$/, "")}/${trace.id}`;
  })();

  return (
    <div className="viewer">
      {(onNavigatePrevious || onNavigateNext) && (
        <div className="viewer__nav">
          <button type="button" onClick={onNavigatePrevious} disabled={!onNavigatePrevious || !hasPrevious}>
            Previous trace
          </button>
          <button type="button" onClick={onNavigateNext} disabled={!onNavigateNext || !hasNext}>
            Next trace
          </button>
        </div>
      )}
      <header className="viewer__header">
        <div className="viewer__title">
          <h2>Trace {trace.id}</h2>
          <div className="viewer__actions">
            <button type="button" onClick={handleCopy} className={copied ? "copied" : ""}>
              {copied ? "Copied" : "Copy ID"}
            </button>
            {traceLogLink && (
              <a href={traceLogLink} target="_blank" rel="noreferrer">
                Open in Gateway
              </a>
            )}
            {onDeleteTrace && (
              <button
                type="button"
                className="viewer__delete"
                onClick={() => onDeleteTrace(trace.id)}
                disabled={isDeletingTrace}
              >
                {isDeletingTrace ? "Deleting…" : "Delete"}
              </button>
            )}
          </div>
        </div>
        <div className="viewer__meta">
          <div>
            <span>Created</span>
            <strong>{createdAt}</strong>
          </div>
          {trace.model && (
            <div>
              <span>Model</span>
              <strong>{trace.model}</strong>
            </div>
          )}
          {trace.channel && (
            <div>
              <span>Channel</span>
              <strong>{trace.channel}</strong>
            </div>
          )}
          {metrics.map((metric) => (
            <div key={metric.label}>
              <span>{metric.label}</span>
              <strong>{metric.value}</strong>
            </div>
          ))}
        </div>
      </header>

      <section className="viewer__section">
        <h3>Messages</h3>
        <ol className="steps">
          {steps.map((step) => (
            <MessageItem key={`${trace.id}-${step.stepIndex}`} step={step} />
          ))}
        </ol>
      </section>

      <section className="viewer__section">
        <h3>Tool Calls</h3>
        {toolCalls.length === 0 && <p className="muted">No tool calls captured.</p>}
        {toolCalls.length > 0 && (
          <table className="tool-table">
            <thead>
              <tr>
                <th>Step</th>
                <th>Name</th>
                <th>Status</th>
                <th>Arguments</th>
                <th>Result</th>
              </tr>
            </thead>
            <tbody>
              {toolCalls.map((call, index) => (
                <tr key={`${call.toolName}-${index}`}>
                  <td>{call.stepIndex}</td>
                  <td>{call.toolName ?? "unknown"}</td>
                  <td>{call.success === undefined ? "" : call.success ? "success" : "error"}</td>
                  <td>{call.argumentsJson}</td>
                  <td>{call.resultJson ?? call.errorMessage ?? ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="viewer__section">
        <h3>Annotations</h3>
        {errorMessage && <p className="error-banner">{errorMessage}</p>}
        <AnnotationForm traceId={trace.id} isSubmitting={createMutation.isPending} onSubmit={(payload) => createMutation.mutate(payload)} />
        <AnnotationList
          annotations={annotations}
          onUpdate={(id, payload) => updateMutation.mutate({ id, payload })}
          onDelete={(id) => deleteMutation.mutate(id)}
          isUpdating={updateMutation.isPending}
          isDeleting={deleteMutation.isPending}
        />
      </section>
    </div>
  );
}

function summarizeAnnotations(annotations: Annotation[]): TraceAnnotationCounts {
  const summary: TraceAnnotationCounts = {
    total: 0,
    needsAttention: 0,
    resolved: 0,
    positive: 0,
    informational: 0
  };

  if (!annotations?.length) {
    return summary;
  }

  for (const annotation of annotations) {
    const status = normalizeAnnotationStatus(annotation.status);
    summary.total += 1;
    switch (status) {
      case "needs_attention":
        summary.needsAttention += 1;
        break;
      case "resolved":
        summary.resolved += 1;
        break;
      case "positive":
        summary.positive += 1;
        break;
      case "informational":
        summary.informational += 1;
        break;
      default:
        summary.needsAttention += 1;
        break;
    }
  }

  return summary;
}

function normalizeAnnotationStatus(value?: string): "needs_attention" | "resolved" | "positive" | "informational" {
  if (!value) return "needs_attention";
  const normalized = value.toLowerCase();
  if (normalized === "open") return "needs_attention";
  switch (normalized) {
    case "needs_attention":
    case "pending":
    case "todo":
      return "needs_attention";
    case "resolved":
    case "done":
      return "resolved";
    case "positive":
      return "positive";
    case "informational":
      return "informational";
    default:
      return "needs_attention";
  }
}

interface MessageProps {
  step: TraceStep;
}

function MessageItem({ step }: MessageProps) {
  const content = step.content ?? "";
  const [expanded, setExpanded] = useState(false);
  const previewLength = 240;
  const isLong = content.length > previewLength;
  const display = expanded || !isLong ? content : `${content.slice(0, previewLength)}${isLong ? "…" : ""}`;

  return (
    <li className={`steps__item steps__item--${step.role}`}>
      <header>
        <span className="steps__role">{step.role ?? "unknown"}</span>
        <span className="steps__index">#{step.stepIndex}</span>
        {isLong && (
          <button className="steps__toggle" type="button" onClick={() => setExpanded((value) => !value)}>
            {expanded ? "Collapse" : "Expand"}
          </button>
        )}
      </header>
      <pre>{display}</pre>
    </li>
  );
}
